import { format } from "date-fns";
import { toast } from "react-toastify";

import type { Asset } from "@/Dashboard/pages/AssetLibrary/types";
import { SHARE_SCOPE } from "@/constants";
import { ExperienceMediaItemService, ExperienceService } from "@/services";

import { getInitialsFromName } from "./user-utils";

export async function addExperienceMediaItems(
  propertyId: string,
  experienceId: string,
  mediaItems: Asset[],
  opts: {
    showToast?: boolean;
  } = {},
) {
  const { showToast = true } = opts;

  if (mediaItems.length) {
    try {
      if (mediaItems.length < 3) {
        await Promise.all(
          mediaItems.map((item) =>
            ExperienceMediaItemService.addExperienceMediaItem(propertyId, experienceId, item.id),
          ),
        );
      } else {
        await ExperienceMediaItemService.bulkAddExperienceMediaItem(propertyId, experienceId, {
          mediaItemIds: mediaItems.map((mediaItem) => mediaItem.id),
        });
      }

      if (showToast) {
        toast.success(`Added ${mediaItems.length} media item${mediaItems.length > 1 ? "s" : ""}`, {
          containerId: "dark",
          autoClose: 1500,
        });
      }
    } catch {
      if (showToast) {
        toast.error(`Failed to add media items.`, { containerId: "dark" });
      }
    }
  }
}

export async function removeExperienceMediaItems(propertyId: string, experienceId: string, mediaItems: Asset[]) {
  if (mediaItems.length) {
    try {
      if (mediaItems.length < 3) {
        await Promise.all(
          mediaItems.map((item) =>
            ExperienceMediaItemService.deleteExperienceMediaItem(propertyId, experienceId, item.id),
          ),
        );
      } else {
        await ExperienceMediaItemService.bulkDeleteExperienceMediaItem(propertyId, experienceId, {
          mediaItemIds: mediaItems.map((mediaItem) => mediaItem.id),
        });
      }

      toast.success(`Removed ${mediaItems.length} media item${mediaItems.length > 1 ? "s" : ""}`, {
        containerId: "dark",
        autoClose: 1500,
      });
    } catch {
      toast.error(`Failed to remove media items.`, { containerId: "dark" });
    }
  }
}

export async function addExperience(
  organizationId: number | null | undefined,
  propertyId: string,
  values: Record<string, unknown>,
  mediaItems: Asset[] = [],
  opts: Record<string, unknown> = {},
  userName = "",
) {
  const data = {
    title: getNewExperienceName(userName),
    organizationId,
    ...values,
  };

  const { resp } = await ExperienceService.addExperience(Number(propertyId), data);
  const { id: experienceId } = resp || {};

  if (!experienceId) {
    return null;
  }

  if (mediaItems.length) {
    await addExperienceMediaItems(propertyId, String(experienceId), mediaItems, opts);
  }

  return experienceId;
}

export function getExperienceValues(experience: Record<string, unknown>) {
  return {
    primaryFilter: experience.primaryFilter,
    typeFilterId: experience.typeFilterId,
    tagFilterId: experience.tagFilterId,
    leftDrawer: experience.leftDrawer,
    rightDrawer: experience.rightDrawer,
    gridView: experience.gridView,
    leftDrawerAutoHide: experience.leftDrawerAutoHide,
    rightDrawerAutoHide: experience.rightDrawerAutoHide,
    tagsToggled: experience.tagsToggled,
    contentsOrdered: experience.contentsOrdered,
    experienceContentTypeIds: experience.experienceContentTypeIds,
    experienceTagIds: experience.experienceTagIds,
    contentTypesOrdered: experience.contentTypesOrdered,
    tagsOrdered: experience.tagsOrdered,
    hiddenContentTypes: experience.hiddenContentTypes,
    hiddenTags: experience.hiddenTags,
    tagNames: experience.tagNames,
    carousel: experience.carousel,
    typeTagFilter: experience.typeTagFilter,
    searchField: experience.searchField,
    currentMediaItemId: experience.currentMediaItemId,
    createdBy: experience.createdBy,
    search: experience.search,
    scope: experience.scope || SHARE_SCOPE.EXPERIENCE,
    language: experience.language,
    locked: experience.locked,
    assetCategoriesOrdered: experience.assetCategoriesOrdered,
    assetCategoriesToggled: experience.assetCategoriesToggled,
    assetCategoryFilterId: experience.assetCategoryFilterId,
    assetMenuOpen: experience.assetMenuOpen,
    experienceAssetCategoryIds: experience.experienceAssetCategoryIds,
    experienceAssets: experience.experienceAssets,
    hiddenAssetCategories: experience.hiddenAssetCategories,
    typesToggled: experience.typesToggled,
    assetCategoryNames: experience.assetCategoryNames,
    experienceMenuItems: experience.experienceMenuItems,
  };
}

export function getNewExperienceName(userName = "") {
  return `Experience_${getInitialsFromName(userName)}_${format(new Date(), "MMMMdd")}`;
}
